import type { NavigationSectionGroup } from '@/components/Navigation/NavigationCard';

import type {
  CFormSectionDetailLayoutSection,
  RouteLinkTo,
} from './CFormSectionDetailLayout';

export interface InternalSectionGroup<S> {
  sectionKey: string;
  sectionName: string;
  sections: S[];
}

export function toSectionHash(sectionKey: string): string {
  return sectionKey.toLowerCase().replace(/_/g, '-');
}

export function groupByKey<S extends CFormSectionDetailLayoutSection>(
  sections: S[],
): InternalSectionGroup<S>[] {
  const groupMap = new Map<string, S[]>();

  for (const section of sections) {
    const key = section.sectionKey;
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)?.push(section);
  }

  return Array.from(groupMap.entries()).map(([key, secs]) => ({
    sectionKey: key,
    sectionName: secs[0].name || key,
    sections: secs,
  }));
}

/**
 * groupBySectionKey 用: グループ化後のキーでフラットなナビを構築。
 */
export function buildGroupedNavigationGroups<
  S extends CFormSectionDetailLayoutSection,
>(sections: S[], linkTo: RouteLinkTo): NavigationSectionGroup[] {
  const groups = groupByKey(sections);
  return [
    {
      titleLabel: '',
      to: linkTo,
      sections: groups.map((group) => ({
        label: group.sectionName,
        hash: toSectionHash(group.sectionKey),
      })),
    },
  ];
}

/**
 * API の displayOrder 順を維持しながら、sectionParentKey でグループ化する。
 *
 * sectionParentKey を持つセクションは sectionParentName を titleLabel とし、
 * 配下のセクションを NavigationSection として展開する。
 * sectionParentKey を持たないセクションはスタンドアロンのリンクとして扱う。
 *
 * グループの出現位置は、そのグループに属する最初のセクションの displayOrder で決まる。
 */
export function buildNavigationGroups<
  S extends CFormSectionDetailLayoutSection,
>(sections: S[], linkTo: RouteLinkTo): NavigationSectionGroup[] {
  const groups: NavigationSectionGroup[] = [];
  const parentGroupMap = new Map<string, NavigationSectionGroup>();

  for (const section of sections) {
    const parentKey = section.sectionParentKey;

    if (parentKey) {
      const existing = parentGroupMap.get(parentKey);
      if (existing) {
        existing.sections?.push({
          label: section.name,
          hash: toSectionHash(section.sectionKey),
        });
      } else {
        const group: NavigationSectionGroup = {
          titleLabel: section.sectionParentName ?? parentKey,
          to: linkTo,
          sections: [
            {
              label: section.name,
              hash: toSectionHash(section.sectionKey),
            },
          ],
        };
        parentGroupMap.set(parentKey, group);
        groups.push(group);
      }
    } else {
      groups.push({
        titleLabel: section.name,
        to: linkTo,
        hash: toSectionHash(section.sectionKey),
      });
    }
  }

  return groups;
}

/**
 * groupBySectionKey + sectionParentKey の組合せ用:
 * まず sectionKey で重複を排除（groupBySectionKey と同じマージ）し、
 * その後 sectionParentKey で親カテゴリ単位にグループ化してナビを構築する。
 *
 * groupBySectionKey=true のページで、目次だけ親カテゴリでグループ表示したい場合に使用。
 */
export function buildGroupedByParentNavigationGroups<
  S extends CFormSectionDetailLayoutSection,
>(sections: S[], linkTo: RouteLinkTo): NavigationSectionGroup[] {
  const groups = groupByKey(sections);
  const uniqueSections = groups.map((g) => g.sections[0]);

  const result: NavigationSectionGroup[] = [];
  const parentGroupMap = new Map<string, NavigationSectionGroup>();

  for (const section of uniqueSections) {
    const parentKey = section.sectionParentKey;

    if (parentKey) {
      const existing = parentGroupMap.get(parentKey);
      if (existing) {
        existing.sections?.push({
          label: section.name,
          hash: toSectionHash(section.sectionKey),
        });
      } else {
        const group: NavigationSectionGroup = {
          titleLabel: section.sectionParentName ?? parentKey,
          to: linkTo,
          sections: [
            {
              label: section.name,
              hash: toSectionHash(section.sectionKey),
            },
          ],
        };
        parentGroupMap.set(parentKey, group);
        result.push(group);
      }
    } else {
      result.push({
        titleLabel: section.name,
        to: linkTo,
        hash: toSectionHash(section.sectionKey),
      });
    }
  }

  return result;
}
